import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../service/API';
import StageSearch from './StageSearch';
import Pill from './Pill';
import '../../styling/service-flow-manager.css';
import { useNavigate, useParams } from 'react-router-dom';
import { TICKET_CLOSED_CODE, TICKET_CLOSED_NAME, isTicketClosed, makeTicketClosed, normalizeWithTicketClosed } from './helpers';
import { getApiErrorMessage } from '../../service/errorHandler';

export default function EditService() {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const { serviceType: serviceTypeParam } = useParams();
    const [initialServiceType, setInitialServiceType] = useState(serviceTypeParam || '');
    const [serviceType, setServiceType] = useState(serviceTypeParam || '');
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState(null);

    const sortedStages = useMemo(() => stages.map((s, i) => ({ ...s, position: i + 1 })), [stages]);
    const showOk = (msg) => setNotice({type:'ok', msg});
    const showErr = (msg) => setNotice({type:'err', msg});

    useEffect(() => {
        let alive = true;
        (async () => {
        try {
            const res = await apiClient.get(`/config/service-flow/${encodeURIComponent(initialServiceType)}`);
            if (!alive) return;
            const dto = res.data;
            setServiceType(dto.serviceType);
            setStages(normalizeWithTicketClosed(dto.stages || []));
        } catch (e) {
            showErr(getApiErrorMessage(e, 'Failed to load service.'));
        } finally {
            if (alive) setLoading(false);
        }
        })();
        return () => { alive = false; };
    }, [initialServiceType]);

    function insertBeforeTicketClosed(prev, item) {
    const idxClosed = prev.findIndex(isTicketClosed);
    if (idxClosed === -1) {
        // if for some reason the sentinel isn't present yet, just append
        return [...prev, item];
    }
    const next = [...prev];
    next.splice(idxClosed, 0, item); // insert right before Ticket Closed
    return next;
    }

    function addExistingStage(picked) {
    if (stages.some((s) => s.id === picked.id)) {
        showErr('This stage is already in the list.');
        return;
    }
    setStages((prev) =>
        insertBeforeTicketClosed(prev, {
        id: picked.id,
        code: picked.code,
        name: picked.name,
        // position should be the index where it lands (before closed)
        position: prev.length, // Ticket Closed shifts to prev.length + 1
        })
    );
    }

    function addNewStage(name) {
    setStages((prev) =>
        insertBeforeTicketClosed(prev, {
        id: null,
        code: null,
        name,
        position: prev.length, // Ticket Closed shifts to prev.length + 1
        })
    );
    }

    function move(idx, dir) {
        setStages((prev) => {
        const next = [...prev]; const j = idx + dir;
        if (j < 0 || j >= next.length) return prev;
        [next[idx], next[j]] = [next[j], next[idx]];
        return next;
        });
    }
    function removeAt(idx) { setStages((prev) => prev.filter((_, i) => i !== idx)); }

    async function save() {
        if (!serviceType.trim()) { showErr('Service name is required.'); return; }
        if (sortedStages.length === 0) { showErr('Add at least one stage.'); return; }

        const payload = {
        serviceType: serviceType.trim(),
        stages: sortedStages.map((s) => ({
            id: s.id ?? undefined,
            name: s.id ? undefined : s.name,
        }))
        };

        try {
        await apiClient.put(`/config/service-flow/${encodeURIComponent(initialServiceType)}`, payload);
        showOk('Service updated.');
        navigate('/service-flows');
        } catch (e) {
            showErr(getApiErrorMessage(e, 'Failed to update service.'));
        }
    }

    if (loading) {
        return (
        <div className="sfm">
            <div className="sfm-container">
            <section className="sfm-card"><p>Loading…</p></section>
            </div>
        </div>
        );
    }

  return (
    <div className="sfm">
        <div className="sfm-container">
            <header style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
            <div>
                <h1>Edit Service</h1>
                <p>Update the service name and its stages.</p>
            </div>
            <div style={{display:'flex', gap:8}}>
                <button className="sfm-btn sfm-btn-secondary" onClick={()=>navigate('/service-flows')}>Back</button>
                <button className="sfm-btn sfm-btn-primary" onClick={save}>Save Changes</button>
            </div>
            </header>

            {notice && (
            <div className={`sfm-notice ${notice.type}`} role="status">
                <span>{notice.msg}</span>
                <button className="sfm-btn sfm-btn-secondary" onClick={()=>setNotice(null)}>✕</button>
            </div>
            )}

            <section className="sfm-card">
            <label htmlFor="svc-edit">Service Name</label>
            <input id="svc-edit" value={serviceType} onChange={(e)=>setServiceType(e.target.value)} type="text" />
            </section>

            <section className="sfm-card">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                    <h2>Stages</h2>
                    <Pill>{sortedStages.length} selected</Pill>
                </div>

                <table className="sfm-stage-table">
                    <thead>
                        <tr>
                            <th style={{width:48}}>#</th>
                            <th style={{width:'20%'}}>Code</th>
                            <th>Name</th>
                            <th style={{width:'15%'}}>Type</th>
                            <th style={{width:'25%', textAlign:'right'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedStages.length === 0 ? (
                            <tr><td colSpan={5} style={{padding:16, opacity:0.7}}>No stages yet. Add below.</td></tr>
                        ) : (
                            sortedStages.map((s, idx)=>(
                            <tr key={`${s.id ?? 'new'}-${idx}`}>
                                <td>{idx+1}</td>
                                <td>{s.code ?? <span style={{opacity:0.6}}>(new)</span>}</td>
                                <td>{s.name}</td>
                                <td>
                                    <button className={`sfm-btn sfm-btn-secondary`} onClick={()=>{}} disabled>
                                        Stage
                                    </button>
                                </td>
                                <td style={{textAlign:'right'}}>
                                <div style={{display:'inline-flex', gap:6}}>
                                    <button className="sfm-btn sfm-btn-secondary" onClick={()=>move(idx,-1)}
                                    disabled={idx===0 || isTicketClosed(s)}>▲</button>
                                    <button className="sfm-btn sfm-btn-secondary" onClick={()=>move(idx,1)}
                                    disabled={idx===sortedStages.length-2 || isTicketClosed(s)}>▼</button>
                                    <button className="sfm-btn sfm-btn-destructive" onClick={()=>removeAt(idx)}
                                    disabled={isTicketClosed(s)}>🗑</button>
                                </div>
                                </td>
                            </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <div style={{marginTop:18}}>
                    <h3 style={{fontSize:16, fontWeight:600, marginBottom:8}}>Add Stages</h3>
                    <StageSearch onPick={addExistingStage} onCreateNew={addNewStage} />
                    <p style={{fontSize:12, marginTop:8}}>
                    Only <b>Booking Creation</b> and <b>Ticket Closed</b> can be terminal when saved.
                    </p>
                </div>
            </section>
        </div>
    </div>
  );
}
